import FeatureFlagsConfig from 'FeatureFlagsConfig';
import { MIN_PRIORITY } from './constants/servicePriorityConstants';
import { shouldFetch } from './FeatureFlagService.utils';

const { FLAGS } = FeatureFlagsConfig;
class FeatureFlagService {
  #featureFlagProviders;

  #providersReady = {};

  #asyncProviders = new Set();

  #syncProviders = new Set();

  #working = null;

  errors = new Set();

  constructor() {
    this.#featureFlagProviders = [];
  }

  get providersReady() {
    return { ...this.#providersReady };
  }

  addProviders(...providers) {
    providers.forEach((provider) => {
      this.#featureFlagProviders.push(provider);
      this.#providersReady[`${provider.constructor.id}_LOADED`] = false;

      if (provider.constructor.async) this.#asyncProviders.add(provider.constructor.id);
      else this.#syncProviders.add(provider.constructor.id);
    });

    this.refreshFeatureFlags(true);
  }

  get isReady() {
    return !this.#asyncProviders.size || !shouldFetch();
  }

  removeProviders(...providerIDs) {
    providerIDs.forEach((provID) => {
      const index = this.#featureFlagProviders.findIndex(
        prov => prov.constructor.id === provID,
      );
      this.#featureFlagProviders.splice(index, 1);
    });
  }

  clearProviders() {
    this.#featureFlagProviders.length = 0;
  }

  getFeatureFlags() {
    return new Promise((resolve) => {
      const featureFlagPromises = [];

      this.#featureFlagProviders.forEach((provider) => {
        featureFlagPromises.push(provider.getFeatureFlags());
      });

      Promise.all(featureFlagPromises).finally(() => {
        resolve();
      });
    });
  }

  /**
   * Trigger a refresh on Feature Flags provider
   *
   * @param {boolean} isInitial
   * @param {Set<string>} forceRefresh
   * @returns void
   */
  refreshFeatureFlags(isInitial = false, forceRefresh = null) {
    let targetProviders = forceRefresh;
    targetProviders ||= !isInitial && shouldFetch() ? this.#asyncProviders : this.#syncProviders;
    const providerIDs = [...targetProviders];

    if (this.#working) return this.#working.then(() => this.refreshFeatureFlags());
    if (providerIDs.length === 0) return Promise.resolve();

    const providers = providerIDs
      .map((providerID) => (
        this.#featureFlagProviders.find(
          prov => prov.constructor.id === providerID,
        )
      ))
      .filter(Boolean);

    const fetchingPromise = new Promise((resolve) => {
      const featureFlagPromises = providers.map((provider) => (
        provider.getFeatureFlags()
      ));

      Promise.all(featureFlagPromises)
        .then(() => {
          this.errors.clear();
          providers
            .filter(provider => provider.error)
            .forEach((provider) => {
              this.errors.add(provider.constructor.id, provider.error);
            });
        })
        .finally(() => {
          providerIDs.forEach((providerID) => {
            this.#providersReady[`${providerID}_LOADED`] = true;
            if (!isInitial) targetProviders.delete(providerID);
          });
          this.#working = null;
          resolve();
        });
    });

    this.#working = fetchingPromise;

    return this.#working;
  }

  isFeatureFlagEnabled(feature) {
    let currentPriority = MIN_PRIORITY - 1;
    let enabled = false;
    this.#featureFlagProviders.forEach((provider) => {
      if (
        provider.hasFeatureFlag(feature)
        && provider.priority > currentPriority
      ) {
        enabled = provider.isFeatureFlagEnabled(feature);
        currentPriority = provider.priority;
      }
    });

    return enabled;
  }

  getAllFlags() {
    return Object
      .keys(FLAGS)
      .reduce((allFlags, flag) => ({ ...allFlags, [flag]: this.isFeatureFlagEnabled(flag) }), {});
  }

  hasErrors() {
    return this.errors.size > 0;
  }

  hasError(providerID) {
    return this.errors.has(providerID);
  }

  getFeatureFlag(feature) {
    let currentPriority = MIN_PRIORITY - 1;
    let value;
    this.#featureFlagProviders.forEach((provider) => {
      if (
        provider.hasFeatureFlag(feature)
        && provider.priority > currentPriority
      ) {
        value = provider.features.get(feature);
        currentPriority = provider.priority;
      }
    });

    return value;
  }

  getFeatureFlagMetadata(feature) {
    let currentPriority = MIN_PRIORITY - 1;
    const values = [];
    let selectedProviderId = null;
    this.#featureFlagProviders.forEach((provider) => {
      const value = provider.features.get(feature);
      const providerId = provider.constructor.id;
      const hasFeatureFlag = provider.hasFeatureFlag(feature);
      if (hasFeatureFlag && provider.priority > currentPriority) {
        selectedProviderId = providerId;
        currentPriority = provider.priority;
      }
      values.push({
        hasFeatureFlag,
        providerId,
        selected: false,
        value,
      });
    });
    const selectedItem = values.find((v) => v.providerId === selectedProviderId);
    if (selectedItem) selectedItem.selected = true;

    return values;
  }
}

const instance = new FeatureFlagService();

export default Object.freeze(instance);
